import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  Newspaper, TrendingUp, Sparkles, Users, ShoppingBag, 
  Clock, Eye, Heart, Share2, Hash, ChevronRight, 
  RefreshCw, Zap, Crown, Play, Instagram, X,
  MessageCircle, Send, Loader2, ExternalLink,
  Flame, Star, ArrowRight, Plus, Upload, Image as ImageIcon,
  Tag, Calendar, Trash2, Edit3, MoreHorizontal, Bookmark,
  Flag, Ban, Download
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../components/ui/use-toast';
import { getApiUrl } from '../utils';
import { cacheSet, cacheGet } from '../utils/offlineCache';
import useDragScroll from '../hooks/useDragScroll';

const API_URL = getApiUrl();

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Category icons and colors mapping
const categoryConfig = {
  'VIRAL INFLUENCER': { 
    icon: Crown, 
    gradient: 'from-stone-600 to-stone-500',
    bgColor: 'bg-stone-100',
    textColor: 'text-stone-700'
  },
  'TRENDING PRODUCT': { 
    icon: ShoppingBag, 
    gradient: 'from-suede-500 to-suede-400',
    bgColor: 'bg-suede-50',
    textColor: 'text-suede-600'
  },
  'TIKTOK TREND': { 
    icon: TikTokIcon, 
    gradient: 'from-amber-500 to-amber-600',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-600'
  },
  'CELEBRITY STYLE': { 
    icon: Star, 
    gradient: 'from-suede-600 to-suede-500',
    bgColor: 'bg-suede-50',
    textColor: 'text-suede-600'
  },
  'DROP ALERT': { 
    icon: Zap, 
    gradient: 'from-terracotta-600 to-terracotta-500',
    bgColor: 'bg-terracotta-50',
    textColor: 'text-terracotta-600'
  },
  'INDUSTRY NEWS': { 
    icon: Newspaper, 
    gradient: 'from-stone-600 to-stone-800',
    bgColor: 'bg-stone-100',
    textColor: 'text-stone-600'
  }
};

// Platform icon component
const PlatformIcon = ({ platform, className }) => {
  switch (platform?.toLowerCase()) {
    case 'tiktok':
      return <TikTokIcon className={className} />;
    case 'instagram':
      return <Instagram className={className} />;
    case 'youtube':
      return <Play className={className} />;
    default:
      return <Hash className={className} />;
  }
};

export default function News() {
  const { toast } = useToast();
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const trendingDrag = useDragScroll();
  
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load cached data immediately
  useEffect(() => {
    Promise.all([cacheGet('news_articles'), cacheGet('news_user_posts')]).then(([cachedArticles, cachedPosts]) => {
      if (cachedArticles?.data?.length) { setArticles(cachedArticles.data); setLoading(false); }
      if (cachedPosts?.data?.length) setUserPosts(cachedPosts.data);
    });
  }, []);
  const [generating, setGenerating] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [weekNumber, setWeekNumber] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [dataSources, setDataSources] = useState(null);
  const [trendingHashtags, setTrendingHashtags] = useState([]);
  const [trendChat, setTrendChat] = useState({ show: false, trend: null, messages: [], input: '' });
  const [chatLoading, setChatLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('ALL');
  
  // User Posts / Style Inspiration state
  const [viewMode, setViewMode] = useState('news'); // 'news' or 'community'
  const [userPosts, setUserPosts] = useState([]);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [newPost, setNewPost] = useState({
    title: '',
    description: '',
    images: [],
    tags: [],
    category: 'outfit-inspo'
  });
  const [tagInput, setTagInput] = useState('');
  const [postMenuOpen, setPostMenuOpen] = useState(null); // post.id of open menu
  const [reportReason, setReportReason] = useState('');

  useEffect(() => {
    fetchNewsletter();
    fetchTrendingHashtags();
    loadUserPosts();
  }, []);

  const fetchNewsletter = async () => {
    try {
      if (articles.length === 0) setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/newsletter/current`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.data.needsGeneration) {
        await generateNewsletter();
      } else {
        setArticles(response.data.articles || []);
        setWeekNumber(response.data.weekNumber);
        setGeneratedAt(response.data.generatedAt);
        // Cache articles for offline
        cacheSet('news_articles', response.data.articles || []);
      }
    } catch (error) {
      console.error('Failed to fetch newsletter:', error);
      // Try cache before regenerating
      const cached = await cacheGet('news_articles');
      if (cached?.data?.length > 0) {
        setArticles(cached.data);
      } else {
        await generateNewsletter();
      }
    } finally {
      setLoading(false);
    }
  };

  const generateNewsletter = async (forceRefresh = false) => {
    try {
      setGenerating(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/newsletter/generate`, 
        { forceRefresh },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      setArticles(response.data.articles || []);
      setWeekNumber(response.data.weekNumber);
      setGeneratedAt(response.data.generatedAt);
      setDataSources(response.data.dataSources || null);
    } catch (error) {
      console.error('Failed to generate newsletter:', error);
    } finally {
      setGenerating(false);
      setLoading(false);
    }
  };

  const fetchTrendingHashtags = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/newsletter/trending-hashtags`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setTrendingHashtags(response.data.hashtags || []);
    } catch (error) {
      console.error('Failed to fetch hashtags:', error);
    }
  };

  const analyzeTrend = async (trend) => {
    setTrendChat({ show: true, trend, messages: [], input: '' });
  };

  const sendTrendQuestion = async () => {
    if (!trendChat.input.trim()) return;

    const question = trendChat.input;
    setTrendChat(prev => ({
      ...prev,
      messages: [...prev.messages, { role: 'user', content: question }],
      input: ''
    }));

    setChatLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/newsletter/analyze-trend`, 
        { trend: trendChat.trend, question },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      setTrendChat(prev => ({
        ...prev,
        messages: [...prev.messages, { role: 'assistant', content: response.data.analysis }]
      }));
    } catch (error) {
      console.error('Failed to analyze trend:', error);
    } finally {
      setChatLoading(false);
    }
  };

  const filteredArticles = activeCategory === 'ALL' 
    ? articles 
    : articles.filter(a => a.category === activeCategory);

  // User Posts Functions
  const loadUserPosts = async () => {
    if (!token) return;
    try {
      // Check for localStorage data to migrate
      const localData = localStorage.getItem('newsUserPosts');
      if (localData) {
        try {
          const localPosts = JSON.parse(localData);
          if (localPosts.length > 0) {
            console.log(`🔄 Migrating ${localPosts.length} news posts to cloud...`);
            await axios.post(`${API_URL}/news-posts/migrate`, { posts: localPosts }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            console.log('✅ News posts migrated to cloud');
          }
        } catch (migErr) {
          console.warn('⚠️ Migration failed, data preserved locally:', migErr.message);
        }
        localStorage.removeItem('newsUserPosts');
      }

      // Fetch from API
      const res = await axios.get(`${API_URL}/news-posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserPosts(res.data.posts || []);
      cacheSet('news_user_posts', res.data.posts || []);
    } catch (err) {
      console.error('Failed to load user posts:', err);
      // Fallback to cache
      const cached = await cacheGet('news_user_posts');
      if (cached?.data) setUserPosts(cached.data);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setNewPost(prev => ({
          ...prev,
          images: [...prev.images, reader.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setNewPost(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const addTag = () => {
    if (tagInput.trim() && !newPost.tags.includes(tagInput.trim())) {
      setNewPost(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setNewPost(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const createUserPost = async () => {
    if (!newPost.images.length && !newPost.title) {
      toast({ title: "Error", description: "Please add a title or image", variant: "destructive" });
      return;
    }

    try {
      const res = await axios.post(`${API_URL}/news-posts`, {
        title: newPost.title,
        description: newPost.description,
        images: newPost.images,
        tags: newPost.tags,
        category: newPost.category,
        author: user.name || 'Anonymous'
      }, { headers: { Authorization: `Bearer ${token}` } });

      setUserPosts(prev => [res.data.post, ...prev]);
    
      setNewPost({
        title: '',
        description: '',
        images: [],
        tags: [],
        category: 'outfit-inspo'
      });
      setShowCreatePost(false);
      toast({ title: "Posted!", description: "Your style inspiration has been shared" });
    } catch (err) {
      console.error('Failed to create post:', err);
      toast({ title: "Error", description: "Failed to create post", variant: "destructive" });
    }
  };

  const togglePostLike = async (postId) => {
    const post = userPosts.find(p => p.id === postId);
    if (!post || post.liked) return; // Already liked — only allow liking once
    const updated = userPosts.map(p => 
      p.id === postId 
        ? { ...p, liked: true, likes: p.likes + 1 }
        : p
    );
    setUserPosts(updated);
    try {
      await axios.put(`${API_URL}/news-posts/${postId}/like`, { liked: true }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Failed to update like:', err);
    }
  };

  const togglePostSave = async (postId) => {
    const post = userPosts.find(p => p.id === postId);
    if (!post || post.saved) return;
    
    // Optimistic update
    setUserPosts(prev => prev.map(p => p.id === postId ? { ...p, saved: true } : p));
    
    try {
      await axios.post(`${API_URL}/saved`, {
        product: {
          name: post.title || 'Style Inspo Post',
          brand: 'Style Inspo',
          image_url: post.images?.[0] || null,
          category: post.category || 'outfit-inspo',
        },
        source: 'style-inspo'
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: "Saved!", description: "Added to your Saved Items" });
    } catch (err) {
      console.error('Failed to save post:', err);
      setUserPosts(prev => prev.map(p => p.id === postId ? { ...p, saved: false } : p));
      toast({ title: "Error", description: "Failed to save", variant: "destructive" });
    }
  };

  const sharePost = async (post) => {
    const shareData = {
      title: post.title || 'Style Inspo',
      text: post.description || 'Check out this style inspiration on fashionDisc!',
      url: 'https://www.fashiondisc.app'
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(`${shareData.title} - ${shareData.url}`);
        toast({ title: "Link copied!", description: "Share link copied to clipboard" });
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Share failed:', err);
      }
    }
  };

  const saveToLibrary = async (imageUrl) => {
    try {
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = `fashiondisc-inspo-${Date.now()}.jpg`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast({ title: "Saved!", description: "Image saved to your device" });
    } catch (err) {
      console.error('Failed to save image:', err);
      toast({ title: "Error", description: "Failed to save image", variant: "destructive" });
    }
  };

  const deleteUserPost = async (postId) => {
    try {
      await axios.delete(`${API_URL}/news-posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserPosts(prev => prev.filter(post => post.id !== postId));
      toast({ title: "Deleted", description: "Post removed" });
    } catch (err) {
      console.error('Failed to delete post:', err);
      toast({ title: "Error", description: "Failed to delete post", variant: "destructive" });
    }
  };

  const reportPost = async (postId, reason) => {
    try {
      await axios.post(`${API_URL}/news-posts/${postId}/report`, { reason: reason || 'inappropriate' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPostMenuOpen(null);
      toast({ title: "Reported", description: "Thank you. We'll review this content shortly." });
    } catch (err) {
      console.error('Failed to report post:', err);
      toast({ title: "Error", description: "Failed to report post", variant: "destructive" });
    }
  };

  const blockUser = async (blockedUserId) => {
    try {
      await axios.post(`${API_URL}/news-posts/block-user`, { blockedUserId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserPosts(prev => prev.filter(post => post.userId !== blockedUserId));
      setPostMenuOpen(null);
      toast({ title: "User Blocked", description: "You will no longer see their posts." });
    } catch (err) {
      console.error('Failed to block user:', err);
      toast({ title: "Error", description: "Failed to block user", variant: "destructive" });
    }
  };

  const postCategories = [
    { id: 'outfit-inspo', label: 'Outfit Inspo', color: 'from-terracotta-500 to-amber-500' },
    { id: 'styling-tips', label: 'Styling Tips', color: 'from-stone-600 to-stone-500' },
    { id: 'trend-alert', label: 'Trend Alert', color: 'from-orange-500 to-terracotta-600' },
    { id: 'beauty-look', label: 'Beauty Look', color: 'from-terracotta-500 to-terracotta-400' },
    { id: 'shopping-find', label: 'Shopping Find', color: 'from-olive-500 to-olive-400' },
  ];

  const categories = ['ALL', ...Object.keys(categoryConfig)];

  if (loading) {
    return (
      <div className="min-h-screen pt-20 pb-24 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
        >
          <Sparkles className="w-12 h-12 text-suede-500" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-terracotta-100 to-suede-100 text-terracotta-700 mb-4">
            <Flame className="w-4 h-4" />
            <span className="text-sm font-semibold">Week {weekNumber} • 2026</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-light text-stone-800 mb-4">
            Viral <span className="bg-gradient-to-r from-suede-600 to-suede-500 bg-clip-text text-transparent font-medium">Fashion</span> & <span className="bg-gradient-to-r from-terracotta-600 to-terracotta-500 bg-clip-text text-transparent font-medium">Beauty</span> News
          </h1>
          
          <p className="text-base sm:text-lg text-stone-600 max-w-2xl mx-auto mb-4 px-2">
            Your weekly dose of trending influencers, viral products, and the hottest styles 
            dominating TikTok, Instagram, and beyond ✨
          </p>

          {/* Data Sources Badge */}
          {dataSources && (
            <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
              <span className="text-xs text-stone-500">Powered by real data:</span>
              {dataSources.trendingTopics > 0 && (
                <span className="px-2 py-1 text-xs bg-terracotta-100 text-terracotta-700 rounded-full">
                  📈 Trending Topics ({dataSources.trendingTopics})
                </span>
              )}
              {dataSources.redditPosts > 0 && (
                <span className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded-full">
                  🔥 Reddit ({dataSources.redditPosts})
                </span>
              )}
              <span className="px-2 py-1 text-xs bg-stone-200 text-stone-700 rounded-full">
                ✨ AI Enhanced
              </span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => generateNewsletter(true)}
              disabled={generating}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-suede-600 to-suede-500 text-white rounded-2xl font-semibold shadow-lg shadow-suede-500/30 disabled:opacity-50"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Refresh This Week
                </>
              )}
            </motion.button>

          </div>

          {generatedAt && (
            <p className="text-xs text-stone-400 mt-4">
              Last updated: {new Date(generatedAt).toLocaleString()}
            </p>
          )}
        </motion.div>

        {/* Trending Hashtags Bar */}
        {trendingHashtags.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8 overflow-hidden"
          >
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-terracotta-500" />
              <span className="text-sm font-semibold text-stone-700">Trending Now</span>
            </div>
            <div
              className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide"
              ref={trendingDrag.ref}
              onMouseDown={trendingDrag.onMouseDown}
              onMouseLeave={trendingDrag.onMouseLeave}
              onMouseUp={trendingDrag.onMouseUp}
              onMouseMove={trendingDrag.onMouseMove}
            >
              {trendingHashtags.map((tag, idx) => (
                <motion.button
                  key={idx}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => analyzeTrend(tag.hashtag)}
                  className="flex-shrink-0 flex items-center gap-2 px-4 py-2 glass-card rounded-2xl hover:border-suede-300 transition-all group"
                >
                  <Hash className="w-3.5 h-3.5 text-terracotta-400" />
                  <span className="text-sm font-medium text-stone-700">{tag.hashtag.replace('#', '')}</span>
                  <span className="text-xs text-stone-400">{tag.growth}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}

        {/* View Mode Toggle - News vs Community */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-8"
        >
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setViewMode('news')}
              className={`relative flex flex-col items-center justify-center py-4 rounded-2xl text-sm font-semibold transition-all border-2 ${
                viewMode === 'news'
                  ? 'bg-gradient-to-br from-suede-600 to-suede-500 text-white border-suede-400 shadow-lg shadow-suede-500/20'
                  : 'bg-white text-stone-500 border-stone-200 hover:border-suede-300 hover:bg-stone-50'
              }`}
            >
              <Newspaper className={`w-5 h-5 mb-1.5 ${viewMode === 'news' ? 'text-white' : 'text-stone-400'}`} />
              <span>News Feed</span>
            </button>
            <button
              onClick={() => setViewMode('community')}
              className={`relative flex flex-col items-center justify-center py-4 rounded-2xl text-sm font-semibold transition-all border-2 ${
                viewMode === 'community'
                  ? 'bg-gradient-to-br from-terracotta-500 to-amber-500 text-white border-terracotta-400 shadow-lg shadow-terracotta-500/20'
                  : 'bg-white text-stone-500 border-stone-200 hover:border-terracotta-300 hover:bg-stone-50'
              }`}
            >
              {viewMode !== 'community' && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6, duration: 0.4 }}
                  className="absolute -top-1.5 -right-1.5 flex h-5 w-5"
                >
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-terracotta-400 opacity-50" />
                  <span className="relative inline-flex rounded-full h-5 w-5 bg-gradient-to-r from-terracotta-500 to-amber-500 items-center justify-center text-[9px] text-white font-bold">
                    ✨
                  </span>
                </motion.span>
              )}
              <Sparkles className={`w-5 h-5 mb-1.5 ${viewMode === 'community' ? 'text-white' : 'text-terracotta-400'}`} />
              <span>Style Inspo</span>
              {userPosts.length > 0 && viewMode !== 'community' && (
                <span className="text-[10px] font-normal mt-0.5 text-stone-400">{userPosts.length} post{userPosts.length !== 1 ? 's' : ''}</span>
              )}
            </button>
          </div>
          
          {viewMode === 'community' && token && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 flex justify-center"
            >
              <Button 
                onClick={() => setShowCreatePost(true)}
                className="bg-gradient-to-r from-terracotta-500 to-amber-500 text-white rounded-2xl w-full py-3"
              >
                <Plus className="w-4 h-4 mr-2" />
                Share Your Inspo
              </Button>
            </motion.div>
          )}
        </motion.div>

        {/* Community / Style Inspiration View */}
        {viewMode === 'community' ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Community Posts Header */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-light text-stone-800 mb-2">
                Community <span className="bg-gradient-to-r from-terracotta-500 to-amber-500 bg-clip-text text-transparent font-medium">Style Inspo</span>
              </h2>
              <p className="text-stone-500 text-sm">Share and discover outfit ideas, styling tips, and trend alerts</p>
            </div>

            {userPosts.length === 0 ? (
              <div className="text-center py-16 bg-white/50 rounded-2xl border border-dashed border-terracotta-200">
                <ImageIcon className="w-16 h-16 text-terracotta-200 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-stone-600 mb-2">No Style Posts Yet</h3>
                <p className="text-stone-400 mb-6 max-w-md mx-auto">
                  Be the first to share your outfit inspiration and styling tips!
                </p>
                {token ? (
                  <Button 
                    onClick={() => setShowCreatePost(true)}
                    className="bg-gradient-to-r from-terracotta-500 to-amber-500 text-white rounded-xl"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Share Your First Post
                  </Button>
                ) : (
                  <a href="/login">
                    <Button className="bg-gradient-to-r from-terracotta-500 to-amber-500 text-white rounded-xl">
                      Sign In to Post
                    </Button>
                  </a>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {userPosts.map((post, index) => {
                  const postCat = postCategories.find(c => c.id === post.category) || postCategories[0];
                  return (
                    <motion.article
                      key={post.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="glass-card rounded-2xl overflow-hidden hover:shadow-2xl transition-all group"
                    >
                      {/* Category Header */}
                      <div className={`bg-gradient-to-r ${postCat.color} px-4 py-3 flex items-center justify-between`}>
                        <span className="text-xs font-bold text-white uppercase tracking-wider">
                          {postCat.label}
                        </span>
                        <div className="flex items-center gap-2">
                          {post.isOwner ? (
                            <button
                              onClick={() => deleteUserPost(post.id)}
                              className="p-1 rounded-full bg-white/20 hover:bg-white/30"
                            >
                              <Trash2 className="w-3 h-3 text-white" />
                            </button>
                          ) : (
                            <div className="relative">
                              <button
                                onClick={() => setPostMenuOpen(postMenuOpen === post.id ? null : post.id)}
                                className="p-1 rounded-full bg-white/20 hover:bg-white/30"
                              >
                                <MoreHorizontal className="w-3 h-3 text-white" />
                              </button>
                              {postMenuOpen === post.id && (
                                <div className="absolute right-0 top-8 z-50 bg-white rounded-xl shadow-xl border border-stone-200 py-1 min-w-[160px]">
                                  <button
                                    onClick={() => reportPost(post.id, 'inappropriate')}
                                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-stone-700 hover:bg-stone-50"
                                  >
                                    <Flag className="w-4 h-4 text-amber-500" />
                                    Report Post
                                  </button>
                                  <button
                                    onClick={() => blockUser(post.userId)}
                                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                                  >
                                    <Ban className="w-4 h-4" />
                                    Block User
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Images */}
                      {post.images.length > 0 && (
                        <div className="relative bg-stone-100">
                          <img 
                            src={post.images[0]} 
                            alt={post.title} 
                            className="w-full max-h-[500px] object-contain mx-auto"
                          />
                          {post.images.length > 1 && (
                            <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                              +{post.images.length - 1}
                            </div>
                          )}
                          {/* Save to library button */}
                          <button
                            onClick={() => saveToLibrary(post.images[0])}
                            className="absolute bottom-2 right-2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      )}

                      {/* Content */}
                      <div className="p-5">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-terracotta-500 to-terracotta-400 flex items-center justify-center text-white text-xs font-bold">
                            {post.author?.charAt(0) || 'A'}
                          </div>
                          <span className="text-xs text-stone-500">{post.author}</span>
                          <span className="text-xs text-stone-400">•</span>
                          <span className="text-xs text-stone-400">
                            {new Date(post.createdAt).toLocaleDateString()}
                          </span>
                        </div>

                        {post.title && (
                          <h3 className="text-lg font-bold text-stone-800 mb-2 line-clamp-2">
                            {post.title}
                          </h3>
                        )}

                        {post.description && (
                          <p className="text-sm text-stone-600 mb-3 line-clamp-2">
                            {post.description}
                          </p>
                        )}

                        {/* Tags */}
                        {post.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {post.tags.slice(0, 3).map(tag => (
                              <span key={tag} className="text-xs px-2 py-0.5 bg-terracotta-50 text-terracotta-600 rounded-full">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center justify-between pt-3 border-t border-stone-100">
                          <div className="flex items-center gap-4">
                            <button 
                              onClick={() => togglePostLike(post.id)}
                              className={`flex items-center gap-1 ${post.liked ? 'text-rose-500' : 'text-stone-500 hover:text-rose-400'} transition-colors`}
                            >
                              <Heart className={`w-4 h-4 ${post.liked ? 'fill-current' : ''}`} />
                              <span className="text-xs">{post.likes}</span>
                            </button>
                            <button 
                              onClick={() => togglePostSave(post.id)}
                              className={`flex items-center gap-1 ${post.saved ? 'text-terracotta-500' : 'text-stone-500 hover:text-terracotta-400'} transition-colors`}
                            >
                              <Bookmark className={`w-4 h-4 ${post.saved ? 'fill-current' : ''}`} />
                              <span className="text-xs">{post.saved ? 'Saved' : 'Save'}</span>
                            </button>
                          </div>
                          <button 
                            onClick={() => sharePost(post)}
                            className="text-stone-400 hover:text-stone-600 transition-colors"
                          >
                            <Share2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </motion.article>
                  );
                })}
              </div>
            )}
          </motion.div>
        ) : (
          <>
        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap gap-2 mb-8 justify-center"
        >
          {categories.map((cat) => {
            const config = categoryConfig[cat];
            const isActive = activeCategory === cat;
            return (
              <motion.button
                key={cat}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-2xl text-sm font-semibold transition-all ${
                  isActive 
                    ? cat === 'ALL'
                      ? 'bg-gradient-to-r from-stone-800 to-stone-900 text-white'
                      : `bg-gradient-to-r ${config.gradient} text-white shadow-lg`
                    : 'glass-card text-stone-600 hover:border-suede-300'
                }`}
              >
                {cat === 'ALL' ? 'All Stories' : cat}
              </motion.button>
            );
          })}
        </motion.div>

        {/* Articles Grid */}
        {filteredArticles.length === 0 ? (
          <div className="text-center py-16">
            <Newspaper className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-500">No articles in this category yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.map((article, index) => {
              const config = categoryConfig[article.category] || categoryConfig['INDUSTRY NEWS'];
              const IconComponent = config.icon;
              
              return (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  onClick={() => setSelectedArticle(article)}
                  className="glass-card rounded-2xl overflow-hidden hover:shadow-2xl transition-all cursor-pointer group"
                >
                  {/* Category Header */}
                  <div className={`bg-gradient-to-r ${config.gradient} px-4 py-3 flex items-center justify-between`}>
                    <div className="flex items-center gap-2">
                      <IconComponent className="w-4 h-4 text-white" />
                      <span className="text-xs font-bold text-white uppercase tracking-wider">
                        {article.category}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Flame className="w-3.5 h-3.5 text-white" />
                      <span className="text-xs font-bold text-white">{article.viralScore}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <PlatformIcon platform={article.platform} className="w-4 h-4 text-stone-400" />
                      <span className="text-xs text-stone-500">{article.platform}</span>
                      <span className="text-xs text-stone-400">•</span>
                      <span className="text-xs text-stone-500">{article.timeAgo}</span>
                      {article.isVerified && (
                        <>
                          <span className="text-xs text-stone-400">•</span>
                          <span className="text-xs text-olive-600 font-medium flex items-center gap-1">
                            ✓ Verified
                          </span>
                        </>
                      )}
                    </div>

                    <h3 className="text-lg font-bold text-stone-800 mb-2 group-hover:text-suede-700 transition-colors line-clamp-2">
                      {article.headline}
                    </h3>

                    <p className="text-sm text-stone-600 mb-4 line-clamp-2">
                      {article.summary}
                    </p>

                    {/* Engagement Stats */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center gap-1">
                        <Eye className="w-3.5 h-3.5 text-stone-400" />
                        <span className="text-xs font-medium text-stone-600">{article.engagement?.views}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Heart className="w-3.5 h-3.5 text-rose-400" />
                        <span className="text-xs font-medium text-stone-600">{article.engagement?.likes}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Share2 className="w-3.5 h-3.5 text-stone-400" />
                        <span className="text-xs font-medium text-stone-600">{article.engagement?.shares}</span>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1.5">
                      {article.tags?.slice(0, 3).map((tag, idx) => (
                        <span 
                          key={idx}
                          className={`px-2 py-0.5 text-xs font-medium rounded-full ${config.bgColor} ${config.textColor}`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Read More Footer */}
                  <div className="px-5 py-3 bg-white/50 flex items-center justify-between">
                    <span className="text-xs font-semibold text-suede-600 group-hover:text-suede-700">
                      Read Full Story
                    </span>
                    <ArrowRight className="w-4 h-4 text-suede-600 group-hover:translate-x-1 transition-transform" />
                  </div>
                </motion.article>
              );
            })}
          </div>
        )}
        </>
        )}

        {/* Create Post Dialog */}
        <Dialog open={showCreatePost} onOpenChange={setShowCreatePost}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-terracotta-500 to-terracotta-400 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                Share Style Inspiration
              </DialogTitle>
              <DialogDescription>
                Post outfit ideas, styling tips, and trend alerts for the community
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Image Upload */}
              <div>
                <Label>Images (optional)</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {newPost.images.map((img, idx) => (
                    <div key={idx} className="relative aspect-square rounded-xl overflow-hidden">
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      <button 
                        onClick={() => removeImage(idx)}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {newPost.images.length < 4 && (
                    <label className="aspect-square rounded-xl border-2 border-dashed border-terracotta-200 flex flex-col items-center justify-center cursor-pointer hover:border-terracotta-400 transition-colors">
                      <Upload className="w-6 h-6 text-terracotta-300 mb-1" />
                      <span className="text-xs text-stone-400">Add</span>
                      <input 
                        type="file" 
                        accept="image/*" 
                        multiple 
                        className="hidden" 
                        onChange={handleImageUpload}
                      />
                    </label>
                  )}
                </div>
              </div>
              
              {/* Title */}
              <div>
                <Label>Title</Label>
                <Input 
                  placeholder="e.g., Perfect Fall Layering Combo"
                  value={newPost.title}
                  onChange={(e) => setNewPost(p => ({ ...p, title: e.target.value }))}
                  className="mt-1"
                />
              </div>
              
              {/* Description */}
              <div>
                <Label>Description</Label>
                <Textarea 
                  placeholder="Share your styling tips or why you love this look..."
                  value={newPost.description}
                  onChange={(e) => setNewPost(p => ({ ...p, description: e.target.value }))}
                  className="mt-1"
                  rows={3}
                />
              </div>
              
              {/* Category */}
              <div>
                <Label>Category</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {postCategories.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => setNewPost(p => ({ ...p, category: cat.id }))}
                      className={`px-3 py-1.5 rounded-full text-sm ${
                        newPost.category === cat.id 
                          ? `bg-gradient-to-r ${cat.color} text-white` 
                          : 'bg-stone-100 text-stone-600'
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Tags */}
              <div>
                <Label>Tags</Label>
                <div className="flex gap-2 mt-1">
                  <Input 
                    placeholder="Add a tag"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button onClick={addTag} variant="outline">Add</Button>
                </div>
                {newPost.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {newPost.tags.map(tag => (
                      <span 
                        key={tag} 
                        className="text-xs px-2 py-1 bg-terracotta-50 text-terracotta-600 rounded-full flex items-center gap-1"
                      >
                        #{tag}
                        <button onClick={() => removeTag(tag)}>
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              <Button 
                onClick={createUserPost}
                className="w-full bg-gradient-to-r from-terracotta-500 to-amber-500 text-white rounded-xl"
                disabled={!newPost.title && newPost.images.length === 0}
              >
                Post Inspiration
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Article Detail Modal */}
        <AnimatePresence>
          {selectedArticle && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedArticle(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl sm:rounded-3xl max-w-2xl w-full max-h-[90vh] sm:max-h-[85vh] overflow-hidden shadow-2xl mx-2"
              >
                {/* Modal Header */}
                <div className={`bg-gradient-to-r ${categoryConfig[selectedArticle.category]?.gradient || 'from-stone-600 to-stone-800'} p-4 sm:p-6`}>
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <div className="flex items-center gap-2">
                      {(() => {
                        const IconComp = categoryConfig[selectedArticle.category]?.icon || Newspaper;
                        return <IconComp className="w-5 h-5 text-white" />;
                      })()}
                      <span className="text-sm font-bold text-white uppercase tracking-wider">
                        {selectedArticle.category}
                      </span>
                    </div>
                    <button
                      onClick={() => setSelectedArticle(null)}
                      className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                    >
                      <X className="w-5 h-5 text-white" />
                    </button>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white">
                    {selectedArticle.headline}
                  </h2>
                </div>

                {/* Modal Content */}
                <div className="p-4 sm:p-6 overflow-y-auto max-h-[60vh]">
                  {/* Meta Info */}
                  <div className="flex flex-wrap items-center gap-4 mb-6 pb-4 border-b border-stone-100">
                    <div className="flex items-center gap-2">
                      <PlatformIcon platform={selectedArticle.platform} className="w-5 h-5 text-stone-500" />
                      <span className="text-sm font-medium text-stone-600">{selectedArticle.platform}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4 text-stone-400" />
                      <span className="text-sm text-stone-500">{selectedArticle.timeAgo}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Flame className="w-4 h-4 text-orange-500" />
                      <span className="text-sm font-bold text-orange-600">Viral Score: {selectedArticle.viralScore}</span>
                    </div>
                  </div>

                  {/* Engagement */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="text-center p-3 glass-card rounded-2xl">
                      <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-suede-500 to-suede-400 flex items-center justify-center mx-auto mb-1">
                        <Eye className="w-4 h-4 text-white" />
                      </div>
                      <p className="text-lg font-bold text-stone-800">{selectedArticle.engagement?.views}</p>
                      <p className="text-xs text-stone-500">Views</p>
                    </div>
                    <div className="text-center p-3 glass-card rounded-2xl">
                      <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-rose-500 to-rose-400 flex items-center justify-center mx-auto mb-1">
                        <Heart className="w-4 h-4 text-white" />
                      </div>
                      <p className="text-lg font-bold text-rose-600">{selectedArticle.engagement?.likes}</p>
                      <p className="text-xs text-stone-500">Likes</p>
                    </div>
                    <div className="text-center p-3 glass-card rounded-2xl">
                      <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-stone-500 to-stone-400 flex items-center justify-center mx-auto mb-1">
                        <Share2 className="w-4 h-4 text-white" />
                      </div>
                      <p className="text-lg font-bold text-stone-600">{selectedArticle.engagement?.shares}</p>
                      <p className="text-xs text-stone-500">Shares</p>
                    </div>
                  </div>

                  {/* Influencer if present */}
                  {selectedArticle.influencer && (
                    <div className="flex items-center gap-3 mb-6 p-3 glass-card rounded-2xl">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-suede-500 to-suede-400 flex items-center justify-center">
                        <Users className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-suede-600 font-medium">Featured Influencer</p>
                        <p className="text-sm font-bold text-stone-800">{selectedArticle.influencer}</p>
                      </div>
                    </div>
                  )}

                  {/* Full Content */}
                  <div className="prose prose-stone prose-sm max-w-none">
                    {selectedArticle.content?.split('\n\n').map((paragraph, idx) => (
                      <p key={idx} className="text-stone-700 leading-relaxed mb-4">
                        {paragraph}
                      </p>
                    ))}
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-stone-100">
                    {selectedArticle.tags?.map((tag, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1 text-xs font-medium bg-stone-100 text-stone-600 rounded-full"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="p-3 sm:p-4 bg-stone-50 border-t border-stone-100 flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-0">
                  <button
                    onClick={() => {
                      setSelectedArticle(null);
                      analyzeTrend(selectedArticle.headline);
                    }}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-suede-700 hover:text-suede-800 transition-colors"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Ask AI About This Trend
                  </button>
                  <button
                    onClick={() => setSelectedArticle(null)}
                    className="px-5 py-2 bg-stone-900 text-white rounded-full text-sm font-semibold hover:bg-stone-800 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Trend Analysis Chat Modal */}
        <AnimatePresence>
          {trendChat.show && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setTrendChat({ show: false, trend: null, messages: [], input: '' })}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl max-w-xl w-full max-h-[80vh] overflow-hidden shadow-2xl flex flex-col"
              >
                {/* Chat Header */}
                <div className="bg-gradient-to-r from-suede-600 to-suede-500 p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-white/80 font-medium">Trend Analysis AI</p>
                      <h3 className="text-lg font-bold text-white">{trendChat.trend}</h3>
                    </div>
                    <button
                      onClick={() => setTrendChat({ show: false, trend: null, messages: [], input: '' })}
                      className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                    >
                      <X className="w-5 h-5 text-white" />
                    </button>
                  </div>
                </div>

                {/* Chat Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[200px]">
                  {trendChat.messages.length === 0 && (
                    <div className="text-center py-8">
                      <Sparkles className="w-10 h-10 text-suede-300 mx-auto mb-3" />
                      <p className="text-stone-500 text-sm">
                        Ask me anything about this trend!
                      </p>
                      <div className="flex flex-wrap gap-2 justify-center mt-4">
                        {['Why did this go viral?', 'How can I recreate this?', 'How long will it last?'].map((q, i) => (
                          <button
                            key={i}
                            onClick={() => setTrendChat(prev => ({ ...prev, input: q }))}
                            className="px-3 py-1.5 text-xs bg-suede-50 text-suede-700 rounded-2xl hover:bg-suede-100 transition-colors"
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {trendChat.messages.map((msg, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[85%] p-3 rounded-2xl ${
                        msg.role === 'user' 
                          ? 'bg-suede-500 text-white rounded-br-md' 
                          : 'bg-stone-100 text-stone-800 rounded-bl-md'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </motion.div>
                  ))}
                  
                  {chatLoading && (
                    <div className="flex justify-start">
                      <div className="bg-stone-100 p-3 rounded-2xl rounded-bl-md">
                        <Loader2 className="w-5 h-5 text-stone-400 animate-spin" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Chat Input */}
                <div className="p-4 border-t border-stone-100">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={trendChat.input}
                      onChange={(e) => setTrendChat(prev => ({ ...prev, input: e.target.value }))}
                      onKeyPress={(e) => e.key === 'Enter' && sendTrendQuestion()}
                      placeholder="Ask about this trend..."
                      className="flex-1 px-4 py-2.5 bg-stone-100 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-suede-500"
                    />
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={sendTrendQuestion}
                      disabled={!trendChat.input.trim() || chatLoading}
                      className="p-2.5 bg-gradient-to-r from-suede-600 to-suede-500 text-white rounded-2xl disabled:opacity-50"
                    >
                      <Send className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </div>
  );
}
